import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export function VetSignIn() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/vet-auth/sign-in/magic-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), callbackURL: '/' }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        setError(body?.message ?? 'Could not send the sign-in link.');
        setSubmitting(false);
        return;
      }
      navigate(`/sign-in/sent?email=${encodeURIComponent(email.trim())}`);
    } catch (_err) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight">Vet portal sign-in</h1>
        <p className="mt-2 text-sm text-zinc-600">
          Enter the email a pet owner used to invite you. We'll email you a sign-in link.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <label className="block text-sm">
            <span className="text-zinc-700">Email</span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-[#004AAD] focus:outline-none focus:ring-1 focus:ring-[#004AAD]"
              placeholder="vet@practice.com"
            />
          </label>
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-[#004AAD] py-2 text-sm font-semibold text-white hover:bg-[#003a8c] disabled:opacity-60"
          >
            {submitting ? 'Sending…' : 'Send sign-in link'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function VetSignInSent() {
  const [params] = useSearchParams();
  const email = params.get('email');
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
        <p className="mt-3 text-sm text-zinc-600">
          {email ? `We sent a sign-in link to ${email}.` : 'We sent you a sign-in link.'}
        </p>
        <p className="mt-2 text-xs text-zinc-500">The link expires shortly.</p>
      </div>
    </div>
  );
}
